import { useMutation } from "@apollo/client";
import InsertDriveFile from "@mui/icons-material/InsertDriveFile";
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import { Dropzone } from "components/Dropzone";
import { DocumentType, WasteInvoiceImportVendor } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useToken } from "providers/token";
import { useCallback, useRef, useState } from "react";
import { ErrorCode, type FileRejection } from "react-dropzone";
import { uploadFile } from "util/uploadFile";

import { QUEUE_WASTE_INVOICE_IMPORT } from "../queries";
import { IMPORT_VENDOR_LABELS } from "../utils";
import {
  acceptedInvoiceExtensions,
  invoiceFileExtension,
  invoiceFileValidationMessage,
} from "./fileRules";

type UploadedDocument = {
  id: string;
  title: string;
};

function useQueueDocument({
  tenantId,
  vendor,
  onClose,
  onQueued,
  setError,
}: {
  tenantId: string;
  vendor: WasteInvoiceImportVendor;
  onClose: () => void;
  onQueued: (runId: string) => void;
  setError: (error: string | null) => void;
}) {
  const [queueImport, { loading }] = useMutation(QUEUE_WASTE_INVOICE_IMPORT);
  const queueDocument = useCallback(
    async (document: UploadedDocument) => {
      try {
        const result = await queueImport({
          variables: { input: { tenantId, sourceDocumentId: document.id, vendor } },
        });
        const runId = result.data?.queueWasteInvoiceImport.id;
        if (!runId) throw new Error("The import did not return a run identifier.");
        onQueued(runId);
        onClose();
      } catch (queueError) {
        const message =
          queueError instanceof Error ? queueError.message : "Unable to queue import.";
        setError(
          `${message} The uploaded document is ready; retrying will not upload the workbook again.`
        );
      }
    },
    [onClose, onQueued, queueImport, setError, tenantId, vendor]
  );
  return { queueDocument, queueing: loading };
}

function useInvoiceSubmission({
  tenantId,
  vendor,
  file,
  uploadedDocument,
  onClose,
  onQueued,
  setError,
  setUploadedDocument,
}: {
  tenantId: string;
  vendor: WasteInvoiceImportVendor;
  file: File | null;
  uploadedDocument: UploadedDocument | null;
  onClose: () => void;
  onQueued: (runId: string) => void;
  setError: (error: string | null) => void;
  setUploadedDocument: (document: UploadedDocument | null) => void;
}) {
  const { user } = useCurrentUser();
  const { getIdTokenRefreshIfExpired } = useToken();
  const [uploading, setUploading] = useState(false);
  const submitInFlight = useRef(false);
  const { queueDocument, queueing } = useQueueDocument({
    tenantId,
    vendor,
    onClose,
    onQueued,
    setError,
  });
  const submit = useCallback(async () => {
    if (submitInFlight.current) return;
    submitInFlight.current = true;
    try {
      if (uploadedDocument) return await queueDocument(uploadedDocument);
      if (!file || !user?.id) return;
      const validationMessage = invoiceFileValidationMessage(file.name, vendor);
      if (validationMessage) return setError(validationMessage);
      setUploading(true);
      setError(null);
      try {
        const idToken = await getIdTokenRefreshIfExpired();
        const document = await uploadFile(
          file,
          {
            tenantId,
            userId: user.id,
            documentType: DocumentType.Invoice,
            description: `${IMPORT_VENDOR_LABELS[vendor]} waste invoice import`,
          },
          idToken
        );
        const uploaded = { id: document.id, title: document.title };
        setUploadedDocument(uploaded);
        await queueDocument(uploaded);
      } catch (uploadError) {
        setError(
          uploadError instanceof Error ? uploadError.message : "Unable to upload invoice workbook."
        );
      } finally {
        setUploading(false);
      }
    } finally {
      submitInFlight.current = false;
    }
  }, [
    file,
    getIdTokenRefreshIfExpired,
    queueDocument,
    setError,
    setUploadedDocument,
    tenantId,
    uploadedDocument,
    user?.id,
    vendor,
  ]);
  return { submit, busy: uploading || queueing, userId: user?.id };
}

function SelectedWorkbook({
  file,
  uploadedDocument,
}: {
  file: File | null;
  uploadedDocument: UploadedDocument | null;
}) {
  if (!file && !uploadedDocument) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <InsertDriveFile color="action" />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {uploadedDocument?.title ?? file?.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {uploadedDocument
            ? "Uploaded and ready to queue"
            : file
              ? `${invoiceFileExtension(file.name).toUpperCase()} · ${formatFileSize(file.size)}`
              : "Ready to upload"}
        </Typography>
      </Box>
    </Stack>
  );
}

function ImportDialogForm({
  open,
  vendor,
  file,
  uploadedDocument,
  error,
  busy,
  userId,
  onClose,
  onSubmit,
  onVendorChange,
  onDrop,
  onDropRejected,
}: {
  open: boolean;
  vendor: WasteInvoiceImportVendor;
  file: File | null;
  uploadedDocument: UploadedDocument | null;
  error: string | null;
  busy: boolean;
  userId: string | undefined;
  onClose: () => void;
  onSubmit: () => void;
  onVendorChange: (value: string) => void;
  onDrop: (files: File[]) => void;
  onDropRejected: (rejections: FileRejection[]) => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      title="Import waste invoices"
      description="Upload a vendor workbook. Structurally valid workbooks are imported automatically; missing facility and manifest matches do not block the import."
      maxWidth="sm"
      fullWidth
      primaryActionButtonProps={{
        saveText: uploadedDocument ? "Retry queueing" : "Upload and import",
        loading: busy,
        disabled: busy || (!file && !uploadedDocument) || !userId,
        onClick: onSubmit,
      }}
    >
      <FormControl disabled={busy || uploadedDocument != null}>
        <FormLabel>Vendor format</FormLabel>
        <RadioGroup row value={vendor} onChange={(event) => onVendorChange(event.target.value)}>
          {Object.values(WasteInvoiceImportVendor).map((value) => (
            <FormControlLabel
              key={value}
              value={value}
              control={<Radio />}
              label={IMPORT_VENDOR_LABELS[value]}
            />
          ))}
        </RadioGroup>
      </FormControl>
      <Dropzone
        label={`Choose or drop ${acceptedInvoiceExtensions(vendor).join(" / ")} workbook`}
        dragActiveText="Drop the invoice workbook here"
        accept={acceptedInvoiceExtensions(vendor)}
        onDrop={onDrop}
        onDropRejected={onDropRejected}
        displayUploadErrors={false}
        multiple={false}
        disabled={busy || uploadedDocument != null}
      />
      <SelectedWorkbook file={file} uploadedDocument={uploadedDocument} />
      {error && <Alert severity="error">{error}</Alert>}
    </Dialog>
  );
}

function DiscardImportDialog({
  open,
  uploaded,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  uploaded: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <ConfirmDialog
      open={open}
      onClose={onCancel}
      onCancel={onCancel}
      onConfirm={onConfirm}
      header="Discard this import?"
      info={
        uploaded
          ? "The workbook is already uploaded, but no import is being tracked. Closing will discard the retry state."
          : "The selected workbook has not been queued. Closing will discard this selection."
      }
      confirmText="Discard import"
      cancelText="Keep working"
    />
  );
}

function useInvoiceFileSelection() {
  const [vendor, setVendor] = useState(WasteInvoiceImportVendor.CleanEarth);
  const [file, setFile] = useState<File | null>(null);
  const [uploadedDocument, setUploadedDocument] = useState<UploadedDocument | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleVendorChange = useCallback(
    (value: string) => {
      let nextVendor: WasteInvoiceImportVendor;
      if (value === WasteInvoiceImportVendor.CleanEarth) {
        nextVendor = WasteInvoiceImportVendor.CleanEarth;
      } else if (value === WasteInvoiceImportVendor.Republic) {
        nextVendor = WasteInvoiceImportVendor.Republic;
      } else {
        return;
      }
      setVendor(nextVendor);
      if (file) {
        const validationMessage = invoiceFileValidationMessage(file.name, nextVendor);
        if (validationMessage) {
          setFile(null);
          setError(`${validationMessage} Choose a workbook for the selected vendor.`);
          return;
        }
      }
      setError(null);
    },
    [file]
  );

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const nextFile = acceptedFiles[0];
      if (!nextFile) return;
      const validationMessage = invoiceFileValidationMessage(nextFile.name, vendor);
      if (validationMessage) {
        setError(validationMessage);
        setFile(null);
        return;
      }
      setFile(nextFile);
      setUploadedDocument(null);
      setError(null);
    },
    [vendor]
  );

  const handleDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      const hasTooManyFiles = rejections.some(({ errors }) =>
        errors.some(({ code }) => code === ErrorCode.TooManyFiles)
      );
      if (hasTooManyFiles) {
        setError("Choose one workbook at a time.");
        return;
      }

      const invalidTypeRejection = rejections.find(({ errors }) =>
        errors.some(({ code }) => code === ErrorCode.FileInvalidType)
      );
      const validationMessage = invalidTypeRejection
        ? invoiceFileValidationMessage(invalidTypeRejection.file.name, vendor)
        : null;
      setError(validationMessage ?? "The selected workbook could not be accepted.");
    },
    [vendor]
  );

  return {
    vendor,
    file,
    uploadedDocument,
    error,
    setError,
    setUploadedDocument,
    handleVendorChange,
    handleDrop,
    handleDropRejected,
  };
}

export function InvoiceImportDialog({
  open,
  tenantId,
  onClose,
  onQueued,
}: {
  open: boolean;
  tenantId: string;
  onClose: () => void;
  onQueued: (runId: string) => void;
}) {
  const {
    vendor,
    file,
    uploadedDocument,
    error,
    setError,
    setUploadedDocument,
    handleVendorChange,
    handleDrop,
    handleDropRejected,
  } = useInvoiceFileSelection();
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const { submit, busy, userId } = useInvoiceSubmission({
    tenantId,
    vendor,
    file,
    uploadedDocument,
    onClose,
    onQueued,
    setError,
    setUploadedDocument,
  });

  const requestClose = useCallback(() => {
    if (file || uploadedDocument) {
      setDiscardConfirmOpen(true);
      return;
    }
    onClose();
  }, [file, onClose, uploadedDocument]);

  return (
    <>
      <ImportDialogForm
        open={open}
        vendor={vendor}
        file={file}
        uploadedDocument={uploadedDocument}
        error={error}
        busy={busy}
        userId={userId}
        onClose={requestClose}
        onSubmit={submit}
        onVendorChange={handleVendorChange}
        onDrop={handleDrop}
        onDropRejected={handleDropRejected}
      />
      <DiscardImportDialog
        open={discardConfirmOpen}
        uploaded={uploadedDocument != null}
        onCancel={() => setDiscardConfirmOpen(false)}
        onConfirm={onClose}
      />
    </>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
